import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const reportQueryKeys = {
  all: ['reports'],
  list: (params) => [...reportQueryKeys.all, 'list', params],
  templateList: (params) => [...reportQueryKeys.all, 'templateList', params],
  templateDetail: (reportTemplateSysId) => [...reportQueryKeys.all, 'templateDetail', reportTemplateSysId],
};

// ==================== Report 요청 목록 조회 ====================
/**
 * Report 요청 목록 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.ReportTitle - 보고서명
 * @param {string} params.StartDate - 시작일
 * @param {string} params.EndDate - 종료일
 * @param {string} params.ReportTemplateName - 보고서 템플릿명
 * @param {string} params.ReportTemplateVersion - 보고서 템플릿 버전
 * @param {string} params.ReportTemplateSysId - 보고서 템플릿 시스템 ID
 * @param {number} params.Page - 페이지 번호
 * @param {number} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useReportRequestList = (params = {}) => {
  return useQuery({
    queryKey: reportQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get('/doc-management/api/requests', { params });
      return response;
    },
  });
};

// ==================== Report 요청 생성 ====================
/**
 * Report 요청 생성
 * @param {Object} payload - 생성할 Report 요청 데이터
 * @param {string} payload.ReportTitle - 보고서명
 * @param {string} payload.ReportTemplateSysId - 보고서 템플릿 시스템 ID
 */
export const useCreateReportRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await api.post('/doc-management/api/requests', payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });
    },
  });
};

// ==================== Report 요청 삭제 ====================
/**
 * Report 요청 삭제
 * @param {string} reportRequestSysId - Report 요청 시스템 ID
 */
export const useDeleteReportRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportRequestSysId) => {
      const response = await api.delete(`/doc-management/api/requests/${reportRequestSysId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });
    },
  });
};



// ==================== Report 템플릿 조회 ====================
/**
 * Report 템플릿 조회
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.TemplateName - 보고서 템플릿명
 * @param {string} params.Version - 보고서 템플릿 버전
 * @param {number} params.Page - 페이지 번호
 * @param {number} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useReportTemplateList = (params = {}) => {
  return useQuery({
    queryKey: reportQueryKeys.templateList(params),
    queryFn: async () => {
      const response = await api.get('/doc-management/api/template', { params });
      return response;
    },
  });
};

// ==================== Report 템플릿 단일 조회 ====================
/**
 * Report 템플릿 단일 조회
 * @param {string} reportTemplateSysId - Report 템플릿 시스템 ID
 */
export const useReportTemplateDetail = (reportTemplateSysId) => {
  return useQuery({
    queryKey: reportQueryKeys.templateDetail(reportTemplateSysId),
    queryFn: async () => {
      const response = await api.get(`/doc-management/api/template/${reportTemplateSysId}`);
      return response;
    },
    enabled: !!reportTemplateSysId,
  });
};

// ==================== Report 템플릿 생성 ====================
/**
 * Report 템플릿 생성
 * @param {Object} payload - 생성할 Report 템플릿 데이터
 * @param {string} payload.templateName - 보고서 템플릿명
 * @param {string} payload.templateVersion - 보고서 템플릿 버전
 * @param {string} payload.templateDescription - 보고서 템플릿 설명
 * @param {File[]} payload.templateFiles - 보고서 템플릿 파일 배열
 * @param {string} [payload.templateType] - 템플릿 타입 (API 필드 Type)
 */
export const useCreateReportTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const files = Array.isArray(payload.templateFiles)
        ? payload.templateFiles.filter((file) => file instanceof File)
        : [];

      // File 배열이 있으면 FormData를 사용하여 multipart/form-data로 전송
      if (files.length > 0) {
        const formData = new FormData();
        formData.append('TemplateName', payload.templateName);
        formData.append('Version', payload.templateVersion);
        formData.append('Description', payload.templateDescription || '');
        if (payload.templateType != null && String(payload.templateType).trim() !== '') {
          formData.append('Type', String(payload.templateType));
        }

        files.forEach((file) => formData.append('Files', file));

        const response = await api.post('/doc-management/api/template', formData);
        return response;
      }

      const body = {
        TemplateName: payload.templateName,
        Version: payload.templateVersion,
        Description: payload.templateDescription || '',
      };
      if (payload.templateType != null && String(payload.templateType).trim() !== '') {
        body.Type = payload.templateType;
      }
      const response = await api.post('/doc-management/api/template', body);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });
    },
  });
};

// ==================== Report 템플릿 삭제 ====================
/**
 * Report 템플릿 삭제
 * @param {string} reportTemplateSysId - Report 템플릿 시스템 ID
 */
export const useDeleteReportTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reportTemplateSysId) => {
      const response = await api.delete(`/doc-management/api/template/${reportTemplateSysId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });
    },
  });
};

// ==================== Report 템플릿 수정 ====================
/**
 * Report 템플릿 수정
 * @param {Object} payload - 수정할 Report 템플릿 데이터
 * @param {string} payload.reportTemplateSysId - Report 템플릿 시스템 ID
 * @param {string} payload.templateName - 보고서 템플릿명
 * @param {string} payload.templateVersion - 보고서 템플릿 버전
 * @param {string} payload.templateDescription - 보고서 템플릿 설명
 * @param {File[]} payload.templateFiles - 보고서 템플릿 파일 배열
 */
export const useUpdateReportTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      // File 배열이 있으면 FormData를 사용하여 multipart/form-data로 전송
      if (Array.isArray(payload.templateFiles) && payload.templateFiles.length > 0) {
        const formData = new FormData();
        formData.append('TemplateName', payload.templateName);
        formData.append('Version', payload.templateVersion);
        formData.append('Description', payload.templateDescription || '');
        
        // 여러 파일을 Files 필드로 추가
        payload.templateFiles
          .filter(file => file instanceof File)
          .forEach(file => formData.append('Files', file));
        
        // axios는 FormData를 자동으로 감지하여 multipart/form-data로 전송
        // Content-Type 헤더를 명시하지 않으면 axios가 boundary를 자동으로 설정함
        const response = await api.put(`/doc-management/api/template/${payload.reportTemplateSysId}`, formData);
        return response;
      } else {
        // File 배열이 없으면 일반 JSON으로 전송
        const response = await api.put(`/doc-management/api/template/${payload.reportTemplateSysId}`, {
          TemplateName: payload.templateName,
          Version: payload.templateVersion,
          Description: payload.templateDescription || '',
        });
        return response;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportQueryKeys.all });
    },
  });
};